import React, { useState } from 'react';

interface InviteMemberModalProps {
  onClose: () => void;
  onInvite: (email: string, role: string) => Promise<void>;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({ onClose, onInvite }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Trainer');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Product decision (2026-07-14): the UI offers the two roles that matter —
  // Trainer (teach + learners + analytics) and Admin (everything incl.
  // billing). Analyst/Billing/Integrator stay valid backend roles but are
  // not offered here.
  const roles = ['Admin', 'Trainer'];

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      await onInvite(email, role);
    } catch (err) {
      setError('Failed to send invitation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-grey-500 bg-opacity-75 dark:bg-grey-900 dark:bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-surface-primary rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-text-primary">
                Invite Team Member
              </h3>
              <div className="mt-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email Input */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-text-primary">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 block w-full rounded-md border-border-primary shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-surface-elevated dark:text-white sm:text-sm"
                      placeholder="newmember@example.com"
                      required
                    />
                  </div>

                  {/* Role Select */}
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-text-primary">
                      Role
                    </label>
                    <select
                      id="role"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="mt-1 block w-full rounded-md border-border-primary shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-surface-elevated dark:text-white sm:text-sm"
                    >
                      {roles.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-text-tertiary">
                      {role === 'Admin' &&
                        'Everything a Trainer can do, plus settings, team, billing & plan'}
                      {role === 'Trainer' &&
                        'Creates courses & programs, manages learners, sees analytics'}
                    </p>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="rounded-md bg-error-50 dark:bg-error-900/20 p-3">
                      <p className="text-sm text-error-700 dark:text-error-400">{error}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse gap-3">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full inline-flex justify-center rounded-full border border-transparent shadow-sm px-4 py-2 bg-primary-500 text-base font-medium hover:bg-primary-600 dark:hover:bg-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900 focus-visible:ring-primary-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Invitation'}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isSubmitting}
                      className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-primary dark:bg-surface-elevated text-base font-medium text-text-primary hover:bg-grey-50 dark:hover:bg-grey-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900 focus-visible:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteMemberModal;
